import React from "react";
import { parseString } from "xml2js";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { ITileDisplayField, IViewsDropdown } from "../../src/taskpane/interfaces/IViewsDropdown";
import authModule from "../../utilities/AuthModule";
import { useLazyGetListColumnsSchemaQuery, useLazyRenderListAsStreamByIDQuery, useLazyRenderListAsStreamGroupDrilldown2Query, useLazyRenderListAsStreamGroupDrilldownQuery, useLazyRenderListAsStreamNextPageQuery, useLazyRenderListAsStreamQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { IColumn, Icon, IconButton, IGroup, IIconStyles, IObjectWithKey, IPersonaStyles, ISpinnerStyles, IStackItemStyles, IStackStyles, IStackTokens, Link, Persona, PersonaSize, Point, Selection, Spinner, SpinnerSize, Stack, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { FileIconType, getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { addWorkingDirectory } from "../../src/taskpane/redux/modules/workingDirectory";
import { IPageSettings } from "../../src/taskpane/interfaces/IPageSettings";
import { useLazyGetItemWebUrlQuery } from "../../src/taskpane/components/_v2/redux/api/graph/api-graph-slice";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { recentFilesAdvancedSearchMapper } from "../../src/taskpane/components/mappers/recentFilesAdvancedSearchMapper";
import { getFileItemIdFromRelativePath, getItemWebViewUrl, getListItemWebUrl } from "../../src/taskpane/services/GraphService";
import { DocubirdCache, extensions, officeFileType, OneNoteProgId, pdfEmbed, rowLockIndicator, shared, SharePointFolderContentTypeIds, sharePointInternalFileName } from "../../src/taskpane/config/constants";
import { setContentManagerFilterFields, setContentManagerFilterSections, toggleContentManagerReload } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { detailRowLinkStyles, fileIconHeaderIcon } from "../../src/taskpane/config/controlStyles";
import { getSharePointContentTypesWithFields, getSharepointListSchema, getSharePointListViews, getSiteLocaleInfo } from "../../src/taskpane/services/SharePointRestServices";
import { setCanViewFolder } from "../../src/taskpane/redux/modules/currentSharepointFolder";
import { detailsListClassNames, IExternalResourceUrl } from "./ContentBodyFilesFolders";
import { logger } from "../../utilities/logger";
import { SearchOpenExtensionHelper } from "../../src/taskpane/components/helpers/SearchOpenExtensionHelper";
import { boundField, currentSharepointFolderState, currentSharepointFolderUserPermissions } from "../../src/taskpane/redux/models/reduxModels";
import { tryDecodeUriComponent } from "../../utilities/EncodeDecode";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DocumentTemplateTypes } from "./MenuItems/useMenuItem_New";
import { ISearchOpenFileInfo } from "../../src/taskpane/interfaces/ISearchOpenFileInfo";
import { SharepointContentTypeHelper } from "../../src/taskpane/components/helpers/sharepointContentTypeHelper";
import directoryViewModule from "../../utilities/DirectoryViewModule";
import { getExtension, splitStringByNewline } from "../../src/taskpane/components/helpers/stringHelper";
import { getGridItemUrl, openInHostDocubird, tryGetUrl } from "../../src/taskpane/components/helpers/UrlHelper";
import { tryGetPathname } from "../../src/taskpane/components/helpers/urlPathname";
import { NewDocumentTemplate } from "../../src/taskpane/interfaces/IDocumentTemplate";
import { CacheHelper } from "../../utilities/CacheHelper";
import { CheckinCheckoutHelper } from "../../utilities/CheckinCheckoutHelper";
import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { ItemBroadcastStatus, ItemStatusBroadcast } from "../../src/taskpane/interfaces/signalR/ItemStatusBroadcast";
import { ImageRenderer } from "../../src/taskpane/components/_v2/components/ImageRenderer/ImageRenderer";
import { ItemHubEvents, ItemHubMethods } from "../../src/taskpane/interfaces/signalR/IItemHub";
import { DatasourceHelper } from "../../src/taskpane/components/helpers/dataSourceHelper";
import { ISiteLocaleInfo } from "../../src/taskpane/interfaces/ISiteLocaleInfo";
import { ColorPair, generateColorForValue } from "../../utilities/DisplayValueFormatUtil";
import { filterStrictSearchResults } from "../../utilities/SearchUtil";
import { useCurrentFolderPermissions } from "./hooks/useCurrentFolderPermissions";
import { buildCurrentSharepointFolderState } from "./helpers/currentSharepointFolderStateBuilder";
import {
  getDirectoryPermissionIdentity,
  getDirectoryRequestKey,
  getResolvedSharePointUniqueIdForDirectoryRequest,
} from "./helpers/contentManagerDirectoryIdentity";

dayjs.extend(utc);
dayjs.extend(timezone);

const iconStyles: IIconStyles = {
  root: {
    display: "block"
  }
}

const spinnerStyles: ISpinnerStyles = {
  root: {
    height: 20
  }
}

const gridFileNameStackTokens: IStackTokens = {
  childrenGap: 5
};

// The filename cell is rendered inside a DetailsRow cell that this grid overrides to
// `display: inline-flex` (see rowStyles.cell in ContentBodyFilesFolders.tsx). Inside a flex
// container a flex item defaults to `min-width: auto`, so it refuses to shrink below the
// intrinsic width of its (un-wrappable) content. That keeps the filename at full width,
// suppresses the text-overflow ellipsis, and pushes the trailing status icons / options
// button past the cell's clip boundary so they never show. `minWidth: 0` opts the item out
// of that automatic minimum so it can shrink, ellipsize, and yield room for the siblings.
const gridFileNameStackItemStyles: IStackItemStyles = {
  root: {
    minWidth: 0,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
  }
}

// Applied to the horizontal Stack that wraps the filename + trailing action items. It must
// fill the fixed-width cell (`width: 100%`) so the action items align to the column's right
// edge, and it is itself a flex item of the inline-flex cell, so it likewise needs
// `minWidth: 0` to shrink to the cell width instead of overflowing it.
const gridFileNameStackStyles: IStackStyles = {
  root: {
    width: "100%",
    minWidth: 0
  }
}

const gridOptionButtonStackItemStyles: IStackItemStyles = {
  root: {
    height: "100%",
    marginTop: -11,
    marginBottom: -11
  }
}

const gridColumnMultiValueContainer: React.CSSProperties = {
  display: "inline-flex",
  flexDirection: "column",
  maxWidth: "100%"
}

const gridRowMultiValueStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center"
}

export const pillContainer: React.CSSProperties = {
  display: "inline-flex",
  flexWrap: "wrap",
  gap: 5,
  maxWidth: "100%"
}

export const pillStyle: React.CSSProperties = {
  boxSizing: "border-box",
  padding: "4px 8px 5px 8px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  height: 24,
  borderRadius: 16,
  display: "inline-flex",
  alignItems: "center"
};

export const peoplePill: React.CSSProperties = {
  backgroundColor: "#edebe9",
  height: 24,
  borderRadius: 12,
  display: "inline-flex",
  alignItems: "center"
}

export const peoplePillPersonaStyles: Partial<IPersonaStyles> = {
  details: {
    paddingLeft: 5
  },
  primaryText: {
    fontSize: 12,
    lineHeight: 18
  }
}

export interface GridSortProps {
  field: string,
  decending: boolean
}

export interface GridGroupProps {
  collapse?: boolean,
  limit?: number
  names?: string[]
}

export interface IGridGroup extends IGroup {
  totalCount?: number;
  urlencoded?: string;
  parentKey?: string;
  filterField?: string;
  filterValue?: string;
}

export interface IGridColumn extends IColumn {
  internalName: string;
}

export interface GridProps {
  sort: GridSortProps[],
  columns?: IGridColumn[];
  items?: any[];
  groups?: IGridGroup[];
  groupProps?: GridGroupProps;
  lastIndex?: number;
  nextUrl?: string;
  hasMoreResults?: boolean;
}

interface IDocumentSetDefaultView {
  contentTypeId: string;
  viewId: string;
}

export const ViewTypes = {
  TILES: "TILES",
};

const getTileDisplayFieldNames = (data: object): Set<string> => {
  const potentialFields = new Set<string>();
  const iterators = new Set<string>();

  // Regex to find field names inside patterns like [$Field] or [!Field.DisplayName]
  const fieldRegex = /(?<=\[\$|\[!)(\w+)/g;

  const findFields = (obj: any) => {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    // Check every key-value pair in the current object
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];

        if (typeof value === 'string') {
          // If the key is 'forEach', it defines an iterator
          if (key === 'forEach') {
            const iteratorName = value.split(' ')[0];
            iterators.add(iteratorName);
          }

          // Find all potential field matches in the string value
          const matches = value.match(fieldRegex);
          if (matches) {
            matches.forEach(field => potentialFields.add(field));
          }
        } else {
          // If the value is an object or array, traverse it recursively
          findFields(value);
        }
      }
    }
  }

  findFields(data);

  // Remove any names that were identified as iterators
  iterators.forEach(iterator => {
    potentialFields.delete(iterator);
  });

  return potentialFields;
}

function _copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
  const key = columnKey as keyof T;
  return items.slice(0).sort((a: T, b: T) => a && b && ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
}

const checkedOutByYouIconStyle: React.CSSProperties = { color: "#A4262C" };
const missingMetadataIconStyle: React.CSSProperties = {
  color: "#cf3902",
  cursor: "pointer"
};

const titleGridCellStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
}

const textGridCellStyle: React.CSSProperties = {
  whiteSpace: "normal"
}

const noteGridCellStyle: React.CSSProperties = {
  whiteSpace: "break-spaces"
}

const oneDriveMountPoint: string = "AddToOneDrive.MountPoint";

interface IListDetails {
  forceCheckout: boolean;
}

const enlistClientConnection = async (connection: HubConnection, listId: string, connectionId?: string) => {
  try {
    await connection.invoke(ItemHubMethods.EnlistClientConnection, connectionId ?? connection.connectionId, listId);
  } catch (err) {
    console.error(err);
  }
}

export function useContentManagerBody(
  gridSelection: Selection<IObjectWithKey>,
  isSharePointList: boolean,
  triggerContextMenu: (ev: any, mousePosition: Point) => void,
  openFilePreviewFromLink: (item: any) => void,
  openFileInfoPanel: (itemIndex: number) => void,
  openExternalResourceUrl: (externalResourceUrl: IExternalResourceUrl) => void,
  clearItemSelection: () => void
) {
  const languageStrings = React.useContext(LanguageContext);

  const dispatch = useDispatch();

  const workingDirectories = useSelector((state: RootState) => state.workingDirectories?.workingDirectories);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
  const searchValue = useSelector((state: RootState) => state.contentManagerDirectory?.searchValue);
  const toggleReload = useSelector((state: RootState) => state.reload.toggle);
  const toggleCMReload = useSelector((state: RootState) => state.contentManagerDirectory?.toggleReload);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
  const userAccount = useSelector((state: RootState) => state.user.account);
  const filterSections = useSelector((state: RootState) => state.contentManagerDirectory.filterSections);
  const tenantOpenDocumentsInClientApp = useSelector((state: RootState) => state.tenantInfo?.openDocumentsInClientApp);
  const metadataTextColors = useSelector((state: RootState) => state.tenantInfo?.metadataTextColors);
  const datasourceItems = useSelector((state: RootState) => state.tenantInfo?.datasources);

  const listFields = React.useRef<any[]>(null);
  const listColumnsSchema = React.useRef<any>(null);
  const selectedViewId = React.useRef('');
  const selectedViewDirectoryId = React.useRef<string>("");
  const currentDirectoryId = React.useRef('');
  const isAllCollapsedRef = React.useRef<boolean>(false);
  const isFetchingNextPage = React.useRef<boolean>(false);
  const serverRelativePathRef = React.useRef<string>("");

  const [gridProps, setGridProps] = React.useState<GridProps>(null);
  const [rawViews, setRawViews] = React.useState(null);
  const [contentTypes, setContentTypes] = React.useState<any[]>([]);
  const [isFetchingData, setIsFetchingData] = React.useState(false);
  const [viewsWithColumns, setViewsWithColumns] = React.useState<IViewsDropdown[]>([]);
  const [selectedView, setSelectedView] = React.useState<IViewsDropdown>();
  const [scrollToTopToggle, setScrollToTopToggle] = React.useState<boolean>(false);
  const [serverRelPath, setServerRelPath] = React.useState('');
  const [listTitle, setListTitle] = React.useState('');
  const [folderPath, setFolderPath] = React.useState('');
  const [newDocumentTemplates, setNewDocumentTemplates] = React.useState<NewDocumentTemplate[]>();
  const [libraryMinVerEnabled, setLibraryMinVerEnabled] = React.useState<boolean>(false);

  const [getListColumnsSchema] = useLazyGetListColumnsSchemaQuery();
  const [getListAsStream] = useLazyRenderListAsStreamQuery();
  const [getListAsStreamGroupDrillDown] = useLazyRenderListAsStreamGroupDrilldownQuery();
  const [getListAsStreamGroupDrillDown2] = useLazyRenderListAsStreamGroupDrilldown2Query();
  const [getListAsStreamNextPage] = useLazyRenderListAsStreamNextPageQuery();
  const [getItemWebUrl] = useLazyGetItemWebUrlQuery();
  const [renderListAsStreamByID] = useLazyRenderListAsStreamByIDQuery();

  const isInDocumentSet = React.useRef<boolean>(false);
  const documentSetDefaultViews = React.useRef<IDocumentSetDefaultView[]>([]);

  const isInOneDrive = React.useRef(false);
  const isFirstRun = React.useRef(true);
  const siteLocaleInfoRef = React.useRef<ISiteLocaleInfo | null>(null);
  const listHttpRoot = React.useRef<string>();
  const activeListRequestId = React.useRef(0);

  const currentFolderPermissionState = useCurrentFolderPermissions(currentDirectory, oneDriveRootDetails);
  const currentDirectoryRequestKey = React.useMemo(() => getDirectoryRequestKey({
    currentDirectory,
    oneDriveRootDetails,
  }), [currentDirectory, oneDriveRootDetails]);
  const currentResolvedSharePointUniqueId = React.useMemo(() => getResolvedSharePointUniqueIdForDirectoryRequest({
    directoryRequestKey: currentDirectoryRequestKey,
    permissionState: currentFolderPermissionState,
  }), [currentDirectoryRequestKey, currentFolderPermissionState]);
  const currentDirectoryPermissionIdentity = React.useMemo(() => getDirectoryPermissionIdentity({
    currentDirectory,
    oneDriveRootDetails,
    resolvedSharePointUniqueId: currentResolvedSharePointUniqueId
  }), [currentDirectory, oneDriveRootDetails, currentResolvedSharePointUniqueId]);
  const currentSharepointFolderRef = React.useRef(currentSharepointFolder);
  const currentFolderPermissionStateRef = React.useRef(currentFolderPermissionState);
  const currentDirectoryPermissionIdentityRef = React.useRef(currentDirectoryPermissionIdentity);
  const currentDirectoryRequestKeyRef = React.useRef(currentDirectoryRequestKey);
  const currentResolvedSharePointUniqueIdRef = React.useRef(currentResolvedSharePointUniqueId);

  currentSharepointFolderRef.current = currentSharepointFolder;
  currentFolderPermissionStateRef.current = currentFolderPermissionState;
  currentDirectoryPermissionIdentityRef.current = currentDirectoryPermissionIdentity;
  currentDirectoryRequestKeyRef.current = currentDirectoryRequestKey;
  currentResolvedSharePointUniqueIdRef.current = currentResolvedSharePointUniqueId;

  const areFolderPermissionsEqual = React.useCallback((left?: currentSharepointFolderUserPermissions, right?: currentSharepointFolderUserPermissions) => {
    if (left === right) {
      return true;
    }

    if (!left || !right) {
      return false;
    }

    return left.canAddListItem === right.canAddListItem
      && left.canEditListItem === right.canEditListItem
      && left.canDeleteListItem === right.canDeleteListItem
      && left.canOpenItems === right.canOpenItems
      && left.canViewVersions === right.canViewVersions
      && left.canDeleteVersions === right.canDeleteVersions
      && left.canCancelCheckout === right.canCancelCheckout
      && left.canApproveItems === right.canApproveItems
      && left.canManagePermissions === right.canManagePermissions
      && left.canEnumeratePermissions === right.canEnumeratePermissions;
  }, []);

  const shouldSyncCurrentFolderPermissions = React.useCallback((nextFolderState: currentSharepointFolderState) => {
    return currentSharepointFolder.directoryRequestKey !== nextFolderState.directoryRequestKey
      || currentSharepointFolder.permissionIdentityKey !== nextFolderState.permissionIdentityKey
      || currentSharepointFolder.permissionStatus !== nextFolderState.permissionStatus
      || currentSharepointFolder.permissionError !== nextFolderState.permissionError
      || currentSharepointFolder.canCreateFolder !== nextFolderState.canCreateFolder
      || currentSharepointFolder.sharePointUniqueId !== nextFolderState.sharePointUniqueId
      || !areFolderPermissionsEqual(currentSharepointFolder.userPermissions, nextFolderState.userPermissions);
  }, [areFolderPermissionsEqual, currentSharepointFolder]);

  const isActiveListRequest = React.useCallback((requestId: number, requestCurrentDirectoryId?: string, requestViewId?: string) => {
    return requestId === activeListRequestId.current
      && requestCurrentDirectoryId === currentDirectoryId.current
      && (requestViewId ?? "") === (selectedViewId.current ?? "");
  }, []);

  React.useEffect(() => {
    if (currentDirectory) {
      isInOneDrive.current = (currentDirectory.type == ComponentTypes.OneDrive || currentDirectory.type == ComponentTypes.OneDriveSubFolder);
    }
  }, [currentDirectory]);

  React.useLayoutEffect(() => {
    if (!currentDirectory) {
      return;
    }

    const nextFolderState = buildCurrentSharepointFolderState({
      existingFolderState: currentSharepointFolder,
      folderUpdate: {
        sharepointListId: currentDirectory?.sharePointListId,
        sharePointUniqueId: currentResolvedSharePointUniqueId ?? currentDirectory?.sharePointUniqueId
      },
      directoryIdentity: currentDirectoryPermissionIdentity,
      directoryRequestKey: currentDirectoryRequestKey,
      permissionState: currentFolderPermissionState
    });

    if (shouldSyncCurrentFolderPermissions(nextFolderState)) {
      dispatch(setCanViewFolder(nextFolderState));
    }
  }, [
    currentDirectory,
    currentDirectoryRequestKey,
    currentDirectoryPermissionIdentity,
    currentFolderPermissionState,
    currentResolvedSharePointUniqueId,
    currentSharepointFolder,
    dispatch,
    shouldSyncCurrentFolderPermissions
  ]);

  React.useEffect(() => {
    let didCancel = false;

    if (isFirstRun.current) {
      isFirstRun.current = false;
    }
    else {
      clearItemSelection();
      // reloadGrid();

      const fetchSearchData = async () => {
        const loadData: boolean = initGridLoad();
        if (loadData) {
          const listStreamResult = await fetchListStream();
          if (listStreamResult && !didCancel) {
            processListStreamResult(listStreamResult.gridData, listStreamResult.serverRelativePath, listStreamResult.listStreamData, listStreamResult);
          }
        }
      };

      fetchSearchData();
    }

    return () => {
      didCancel = true;
    };

  }, [searchValue, toggleReload, toggleCMReload]);

  React.useEffect(() => {
    currentDirectoryId.current = currentDirectory?.id ?? "";
    dispatch(setContentManagerFilterSections([]));
    dispatch(setContentManagerFilterFields([]));
  }, [currentDirectory?.id]);

  React.useEffect(() => {
    if (currentDirectory && currentDirectory.webUrl) {
      (async () => {
        siteLocaleInfoRef.current = await getSiteLocaleInfo(
          isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory.webUrl
        );
      })();
    }
  }, [currentDirectory?.webUrl]);

  React.useEffect(() => {
    let connection: HubConnection;
    if (currentDirectory && currentDirectory.sharePointListId?.length > 0) {
      (async () => {
        const listDetails = await getViewsAndColumns();

        if (listDetails?.forceCheckout || CacheHelper.get(DocubirdCache.signalREnabled) === true) {
          connection = new HubConnectionBuilder()
            .withUrl(`${process.env.HUB_URL}/itemhub`, { withCredentials: false })
            .withAutomaticReconnect()
            .withStatefulReconnect()
            .configureLogging(LogLevel.None)
            .build();

          const startSignalRConnection = async (connection: HubConnection) => {
            try {
              await connection.start();
              enlistClientConnection(connection, currentDirectory.sharePointListId);
            } catch (err) {
              setTimeout(() => startSignalRConnection(connection), 5000);
            }
          }

          connection.onreconnected((connectionId?: string) => {
            enlistClientConnection(connection, currentDirectory.sharePointListId, connectionId);
          });

          connection.on(ItemHubEvents.ReceiveStatusUpdate, (broadcast: ItemStatusBroadcast) => {
            if (broadcast?.itemId > 0) {
              const itemIdString: string = broadcast.itemId.toString();
              if (broadcast.status === ItemBroadcastStatus.Created) {
                addRow(itemIdString);
              }
              else if (broadcast.status === ItemBroadcastStatus.Deleted) {
                removeRow(itemIdString);
              }
              else if (broadcast.status === ItemBroadcastStatus.Locked) {
                lockRow(itemIdString);
              }
              else if (broadcast.status === ItemBroadcastStatus.ExternalResource) {
                if (broadcast.externalResourceUrl && broadcast.accountId?.toLowerCase() === userAccount.accountId?.toLowerCase()) {
                  const resourceUrlParts: string[] = broadcast.externalResourceUrl.split(", ");
                  const externalResourceUrl: IExternalResourceUrl = {
                    url: resourceUrlParts[0].trim(),
                    description: resourceUrlParts.length > 1
                      ? resourceUrlParts[1].trim()
                      : tryGetUrl(resourceUrlParts[0])?.hostname ?? languageStrings.ExternalContent
                  }
                  openExternalResourceUrl(externalResourceUrl);
                }
              }
              else {
                reloadRow(itemIdString);
              }
            }
          });

          startSignalRConnection(connection);
        }
      })();
    }

    return () => {
      connection?.stop();
    }
  }, [currentDirectory?.sharePointListId]);

  React.useEffect(() => {
    if (selectedView) {
      if (selectedView.isReport) {
        const defaultView = viewsWithColumns?.find(x => x.default);
        selectedViewId.current = defaultView?.Id ?? selectedView.Id;
      } else {
        selectedViewId.current = selectedView.Id;
      }
    } else {
      selectedViewId.current = "";
    }

    selectedViewDirectoryId.current = currentDirectory?.id;

    clearItemSelection();
    dispatch(setContentManagerFilterSections([]));
    dispatch(setContentManagerFilterFields([]));

    if (selectedView) {

      if (!isInOneDrive.current) {
        directoryViewModule.addDirectoryView({ directoryId: currentDirectory?.id, viewId: selectedView.Id, timestamp: new Date().getTime() });
      }

      try {
        const newGridProps = { ...gridProps };
        newGridProps.items = [];
        newGridProps.groups = [];
        newGridProps.sort = [];
        newGridProps.nextUrl = '';
        newGridProps.hasMoreResults = false;

        getOrderByGroupByFromView(newGridProps);

        let viewsColsToRender = []
        if (isInOneDrive.current) {
          viewsColsToRender.push({ Name: 'DocIcon' });
          viewsColsToRender.push({ Name: 'LinkFilename' });
          viewsColsToRender.push({ Name: 'Modified' });
          viewsColsToRender.push({ Name: 'Editor' });
          viewsColsToRender.push({ Name: 'FileSizeDisplay' });
          viewsColsToRender.push({ Name: 'SharedWith' });
        } else {
          viewsColsToRender = [...selectedView.fields];
        }

        const newCols: IGridColumn[] = [];

        viewsColsToRender.forEach((col) => {
          const listCol = listColumnsSchema.current?.value.find((x) => x.InternalName === col?.Name);
          if (listCol) {
            const colSchema: any = convertMetadataToFields(listCol);
            const isInSPList = currentDirectory.type === ComponentTypes.SharePointList;

            let originalColName = colSchema.xml.Name;
            let colName = colSchema.xml.Name;

            if (currentDirectory.type === ComponentTypes.SharePointList && colSchema.xml.DisplayNameSrcField) {
              colName = colSchema.xml.DisplayNameSrcField;
            }

            const colType = colSchema.xml.Type;
            const colFormat = colSchema.xml.Format;
            const colResultType = colSchema.xml.ResultType;

            const newColumn: IGridColumn = {
              key: colSchema.xml.DisplayName,
              name: colSchema.xml.Name === 'DocIcon' ? '\u00A0' : colSchema.xml.DisplayName,
              fieldName: colSchema.xml.DisplayName,
              internalName: colSchema.InternalName,
              minWidth: 150,
              maxWidth: 150,
              isResizable: true
            }

            updateColumnSortProps(newColumn, newGridProps, colSchema);

            if (colName === "_UIVersionString") {
              newColumn.minWidth = 75;
              newColumn.maxWidth = 75;
            }

            if (colName === 'DocIcon') {
              newColumn.iconClassName = fileIconHeaderIcon;
              newColumn.iconName = 'Page';
              newColumn.isIconOnly = true;
              newColumn.minWidth = 20;
              newColumn.maxWidth = 20;

              newColumn.onRender = item => {
                return (
                  <>
                    {item.FSObjType === '0' && !item.isBusy && !isInSPList &&
                      (item.ProgId !== oneDriveMountPoint
                        ? <Icon {...getFileTypeIconProps({ extension: item.File_x0020_Type, size: 20, imageFileType: "png" })} styles={iconStyles} />
                        : <Icon {...getFileTypeIconProps({ type: FileIconType.sharedFolder, size: 20, imageFileType: "png" })} styles={iconStyles} />
                      )
                    }
                    {item.FSObjType === '0' && item.isBusy === true && <Spinner size={SpinnerSize.small} styles={spinnerStyles} />}
                    {item.FSObjType === '0' && !item.isBusy && isInSPList && <Icon {...getFileTypeIconProps({ type: FileIconType.listItem, size: 20, imageFileType: "png" })} styles={iconStyles} />}
                    {item.FSObjType === '1' && item.ProgId !== OneNoteProgId &&
                      <Icon {...getFileTypeIconProps({ type: item.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet) ? FileIconType.docset : FileIconType.folder, size: 20, imageFileType: "svg" })} styles={iconStyles} />
                    }
                    {item.FSObjType === '1' && item.ProgId === OneNoteProgId &&
                      <Icon {...getFileTypeIconProps({ extension: 'onetoc', size: 20, imageFileType: "svg" })} styles={iconStyles} />
                    }
                  </>
                )
              }
            }
            else if (colName === 'LinkFilename' || originalColName === 'LinkTitle') {
              newColumn.minWidth = 260;
              newColumn.maxWidth = 300;
              newColumn.isRowHeader = true;
              newColumn.onRender = (item: any, index: number) => {
                return (
                  <>
                    {
                      (item.FSObjType === '0' || item.ProgId === OneNoteProgId) && originalColName !== 'LinkTitle' &&
                      <Stack horizontal verticalAlign="center" tokens={gridFileNameStackTokens} styles={gridFileNameStackStyles}>
                        <Stack.Item grow styles={gridFileNameStackItemStyles}>
                          {!item[rowLockIndicator]
                            ? <Link
                              data-selection-disabled={true}
                              rel="noopener"
                              styles={detailRowLinkStyles}
                              onClick={async () => {
                                if (item && item.File_x0020_Type === "url") {
                                  const url = await getGridItemUrl(item, item._ShortcutUrl ?? CacheHelper.get(`shortcutLink_${item.UniqueId?.replace("{", "")?.replace("}", "")?.toLowerCase()}`));
                                  window.open(url, "_blank");
                                  return;
                                }

                                let openFile = true;
                                let fileInfo: ISearchOpenFileInfo;
                                if (!currentSharepointFolder?.userPermissions?.canOpenItems && item.File_x0020_Type?.toLowerCase() !== extensions.pdf) {
                                  fileInfo = SearchOpenExtensionHelper.determineOfficeFileType(item.File_x0020_Type);
                                  if (fileInfo?.type === officeFileType.Browser) {
                                    openFile = false;
                                  }
                                }

                                if (openFile) {
                                  let driveId = isInOneDrive.current ? oneDriveRootDetails.driveId : (currentDirectory.parentId ? currentDirectory.parentId : currentDirectory.id);

                                  const spItemUrlparts = item['.spItemUrl'].split('/');
                                  let itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                                  if (!itemId) {
                                    setIsFetchingData(true);
                                    const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
                                    const folderUrl = new URL(driveUrl.origin + item.FileRef);
                                    itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
                                    setIsFetchingData(false);
                                  }

                                  if (item.File_x0020_Type?.toLowerCase() === extensions.pdf) {
                                    window.open(`${process.env.PDF_URL}?${shared.params.accountId}=${userAccount.accountId}&${shared.params.username}=${userAccount.username}&${shared.params.itemId}=${itemId}&${shared.params.driveId}=${driveId}&${pdfEmbed.params.isInOneDrive}=${isInOneDrive.current === true ? '1' : '0'}&${shared.params.language}=${dayjs.locale() ?? navigator.language}`);
                                  }
                                  else if (tenantOpenDocumentsInClientApp && currentSharepointFolder?.userPermissions?.canOpenItems) {
                                    const url = await getListItemWebUrl(await authModule.getGraphAccessToken(), driveId, itemId);

                                    if (!fileInfo) {
                                      fileInfo = SearchOpenExtensionHelper.determineOfficeFileType(item.File_x0020_Type);
                                    }

                                    window.open(`${fileInfo.localUrlPrepended}${url}`, fileInfo.localUrlPrepended ? "_self" : "_blank");
                                  } else {
                                    const url = await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, itemId);
                                    window.open(url, "_blank");
                                  }

                                } else {
                                  openFilePreviewFromLink(item);
                                }

                              }}
                            >
                              <TooltipHost content={item.FileLeafRef} overflowMode={TooltipOverflowMode.Parent}>
                                <span aria-describedby={item.key}>{item.FileLeafRef}</span>
                              </TooltipHost>
                            </Link>
                            : <span aria-describedby={item.key}>{item.FileLeafRef}</span>}
                        </Stack.Item>
                        {item.EffectiveBasePermissions?.canEditListItem === false && <Stack.Item>
                          <TooltipHost content={languageStrings.ReadOnly}>
                            <Icon iconName="Uneditable2" />
                          </TooltipHost>
                        </Stack.Item>}
                        {Array.isArray(item.CheckoutUser) && item.CheckoutUser?.length > 0 && CheckinCheckoutHelper.compareCurrentUser(item.CheckoutUser[0], userAccount.username) && <Stack.Item>
                          <TooltipHost content={languageStrings.CheckedOutByYou}>
                            <Icon iconName="CheckedOutByYou12" style={checkedOutByYouIconStyle} />
                          </TooltipHost>
                        </Stack.Item>}
                        {Array.isArray(item.CheckoutUser) && item.CheckoutUser?.length > 0 && !CheckinCheckoutHelper.compareCurrentUser(item.CheckoutUser[0], userAccount.username) && <Stack.Item>
                          <TooltipHost content={`${languageStrings.CheckedOutBy} ${item.CheckoutUser[0].title}`}>
                            <Icon iconName="CheckedOutByOther12" />
                          </TooltipHost>
                        </Stack.Item>}
                        {checkMissingMetadata(item) && <Stack.Item>
                          <TooltipHost content={languageStrings.MissingMetadata}>
                            <Icon iconName="TagUnknown12" style={missingMetadataIconStyle} onClick={() => openFileInfoPanel(index)} />
                          </TooltipHost>
                        </Stack.Item>}
                        {!item[rowLockIndicator] && <Stack.Item styles={gridOptionButtonStackItemStyles}>
                          <TooltipHost content={languageStrings.MoreActions}>
                            <IconButton
                              aria-label="Options"
                              className={detailsListClassNames.optionButton}
                              iconProps={{ iconName: 'MoreVertical' }}
                              onClick={(ev) => {
                                const pos: Point = { left: ev.clientX, top: ev.clientY };
                                triggerContextMenu(ev, pos);
                              }}
                            />
                          </TooltipHost>
                        </Stack.Item>}
                      </Stack>
                    }
                    {item.FSObjType === '0' && originalColName === 'LinkTitle' &&
                      (currentDirectory.type === ComponentTypes.SharePointList && !item[rowLockIndicator]
                        ? <Stack horizontal verticalAlign="center" tokens={gridFileNameStackTokens} styles={gridFileNameStackStyles}>
                          <Stack.Item grow styles={gridFileNameStackItemStyles}>
                            <Link
                              data-selection-disabled={true}
                              rel="noopener"
                              styles={detailRowLinkStyles}
                              onClick={() => openFileInfoPanel(index)}
                            >
                              <TooltipHost content={item[colName]} overflowMode={TooltipOverflowMode.Parent}>
                                <span aria-describedby={item.key}>{item[colName]}</span>
                              </TooltipHost>
                            </Link>
                          </Stack.Item>
                        </Stack>
                        : <span>{item[colName]}</span>)
                    }
                    {
                      item.FSObjType === '1' && item.ProgId !== OneNoteProgId &&
                      <Stack horizontal verticalAlign="center" tokens={gridFileNameStackTokens} styles={gridFileNameStackStyles}>
                        <Stack.Item grow styles={gridFileNameStackItemStyles}>
                          {!item[rowLockIndicator]
                            ? <Link
                              data-selection-disabled={true}
                              rel="noopener"
                              styles={detailRowLinkStyles}
                              onClick={() => onFolderClick(item)}
                            >
                              <TooltipHost content={item.FileLeafRef} overflowMode={TooltipOverflowMode.Parent}>
                                <span aria-describedby={item.key}>{item.FileLeafRef}</span>
                              </TooltipHost>
                            </Link>
                            : <span aria-describedby={item.key}>{item.FileLeafRef}</span>}
                        </Stack.Item>
                        {item.EffectiveBasePermissions?.canEditListItem === false && <Stack.Item>
                          <TooltipHost content={languageStrings.ReadOnly}>
                            <Icon iconName="Uneditable2" />
                          </TooltipHost>
                        </Stack.Item>}
                        {!item[rowLockIndicator] && <Stack.Item styles={gridOptionButtonStackItemStyles}>
                          <TooltipHost content={languageStrings.MoreActions}>
                            <IconButton
                              aria-label="Options"
                              className={detailsListClassNames.optionButton}
                              iconProps={{ iconName: 'MoreVertical' }}
                              onClick={(ev) => {
                                const pos: Point = { left: ev.clientX, top: ev.clientY };
                                triggerContextMenu(ev, pos);
                              }}
                            />
                          </TooltipHost>
                        </Stack.Item>}
                      </Stack>
                    }
                  </>
                )
              }
            }
            else if (colName === 'FileSizeDisplay') {
              newColumn.onRender = item => {
                let fileSizeDisplay = '';
                if (item.FSObjType === '1') {
                  let childFolderCount = item.FolderChildCount ? +item.FolderChildCount : 0;
                  let childItemsCount = item.ItemChildCount ? +item.ItemChildCount : 0;

                  fileSizeDisplay = `${childFolderCount + childItemsCount} items`
                } else {
                  fileSizeDisplay = recentFilesAdvancedSearchMapper.convertBytes(isInOneDrive.current ? +item.FileSizeDisplay : +item.File_x0020_Size);
                }

                return (
                  <span>{fileSizeDisplay}</span>
                )
              }
            }
            else if (colType === 'MultiChoice') {
              newColumn.onRender = (item) => {
                if (!Array.isArray(item[colName])) return <></>;

                if (listCol.CustomFormatter) {
                  return <div style={pillContainer}>
                    {item[colName].map((item, index) => {
                      const valueColor: ColorPair = generateColorForValue(item, metadataTextColors);
                      return <div key={index} style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
                        <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{item}</span>
                      </div>
                    })}
                  </div>
                }

                return <div style={gridColumnMultiValueContainer}>
                  {item[colName].map((item, index) => (
                    <div key={index} style={gridRowMultiValueStyle}>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              }
            }
            else if (colType === "Choice") {
              newColumn.onRender = (item) => {
                if (item[colName] === '') return <></>;

                if (listCol.CustomFormatter) {
                  const valueColor: ColorPair = generateColorForValue(item[colName], metadataTextColors);
                  return <div style={pillContainer}>
                    <div style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
                      <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{item[colName]}</span>
                    </div>
                  </div>
                }

                return <span style={textGridCellStyle}>{item[colName]}</span>
              }
            }
            else if (colType === "Text") {
              let datasourceBoundField: boundField;

              if (Array.isArray(datasourceItems)) {
                const datasourceField = DatasourceHelper.getInputDataSource(datasourceItems, newColumn.fieldName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""]);
                if (Array.isArray(datasourceField?.boundFields) && datasourceField.boundFields.length > 0) {
                  datasourceBoundField = datasourceField.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === newColumn.fieldName.trim().toLowerCase()));
                }
              }

              if (datasourceBoundField?.multiSelect) {
                newColumn.onRender = (item) => {
                  const colStringValue: string = item[colName];
                  if (!colStringValue) return <></>;

                  const colArrayValue: string[] = colStringValue
                    .split(",")
                    .map(v => v.trim())
                    .filter(Boolean);

                  if (!Array.isArray(colArrayValue) || colArrayValue.length === 0) return <></>;

                  if (listCol.CustomFormatter) {
                    return <div style={pillContainer}>
                      {colArrayValue.map((item, index) => {
                        const valueColor: ColorPair = generateColorForValue(item, metadataTextColors);
                        return <div key={index} style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
                          <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{item}</span>
                        </div>
                      })}
                    </div>
                  }

                  return <div style={gridColumnMultiValueContainer}>
                    {colArrayValue.map((item, index) => (
                      <div key={index} style={gridRowMultiValueStyle}>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                }
              }
              else {
                newColumn.onRender = (item) => {
                  if (item[colName] === '') return <></>;

                  if (listCol.CustomFormatter) {
                    const valueColor: ColorPair = generateColorForValue(item[colName], metadataTextColors, !datasourceBoundField);
                    return <div style={pillContainer}>
                      <div style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
                        <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{item[colName]}</span>
                      </div>
                    </div>
                  }

                  return <span style={colName === "Title" ? titleGridCellStyle : textGridCellStyle}>{item[colName]}</span>
                }
              }
            }
            else if (colType === 'Lookup' || colType === 'LookupMulti') {
              newColumn.onRender = (item) => {
                if (!Array.isArray(item[colName])) return <></>;

                if (listCol.CustomFormatter) {
                  return <div style={pillContainer}>
                    {item[colName].map((item, index) => {
                      const valueColor: ColorPair = generateColorForValue(item.lookupValue, metadataTextColors);
                      return <div key={index} style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
                        <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{item.lookupValue}</span>
                      </div>
                    })}
                  </div>
                }

                return <div style={gridColumnMultiValueContainer}>
                  {item[colName].map((item, index) => (
                    <div key={index} style={gridRowMultiValueStyle}>
                      <span>{item.lookupValue}</span>
                    </div>
                  ))}
                </div>
              }
            }
            else if (colType === 'TaxonomyFieldType') {
              newColumn.onRender = item => {
                return (
                  <span style={textGridCellStyle}>{item[colName]?.Label}</span>
                )
              }
            }
            else if (colType === 'TaxonomyFieldTypeMulti') {
              newColumn.onRender = (item) => (
                <>
                  {Array.isArray(item[colName]) && <div style={gridColumnMultiValueContainer}>
                    {item[colName].map((item, index) => (
                      <div key={index} style={gridRowMultiValueStyle}>
                        <span>{item.Label}</span>
                      </div>
                    ))}
                  </div>}
                </>
              )
            }
            else if (colType === 'DateTime') {
              newColumn.onRender = (item) => (
                <>
                  {/* {item[colName] && <span>{colFormat === 'DateOnly' ? dayjs(item[colName]).utc().local().format("L") : dayjs(item[colName]).utc().local().format("L LT")}</span>} */}
                  {item[colName] && <span>{colFormat === 'DateOnly' ? getTimeZoneFormattedDate(item[colName], "L") : getTimeZoneFormattedDate(item[colName], "L LT")}</span>}
                </>
              )
            }
            else if (colType === 'URL') {
              newColumn.onRender = (item) => {
                const colUrl = item[colName];
                return (
                  <>
                    {colUrl && <span><Link onClick={(async (e) => {
                      e.preventDefault();
                      if (colUrl.endsWith && colUrl.endsWith(openInHostDocubird)) {
                        const url: string = colUrl
                          .replace(`?${openInHostDocubird}`, "")
                          .replace(`&${openInHostDocubird}`, "")
                          .trim();
                        const externalResourceUrl: IExternalResourceUrl = {
                          url,
                          description: item[`${colName}.desc`] && item[`${colName}.desc`] !== colUrl
                            ? item[`${colName}.desc`]
                            : tryGetUrl(url)?.hostname ?? languageStrings.ExternalContent
                        }
                        openExternalResourceUrl(externalResourceUrl);
                      }
                      else {
                        const url = await getGridItemUrl(item, colUrl, listHttpRoot.current);
                        window.open(url, "_blank");
                      }
                    })}>{item[`${colName}.desc`] || item[colName]}</Link></span>}
                  </>
                )
              }
            }
            else if (colType === 'Calculated' && (colResultType === 'DateOnly' || colResultType === 'DateTime')) {
              newColumn.onRender = (item) => (
                <>
                  {/* {item[colName] && <span>{colFormat === 'DateOnly' ? dayjs(item[colName]).utc().local().format("L") : dayjs(item[colName]).utc().local().format("L LT")}</span>} */}
                  {item[colName] && <span>{colFormat === 'DateOnly' ? getTimeZoneFormattedDate(item[colName], "L") : getTimeZoneFormattedDate(item[colName], "L LT")}</span>}
                </>
              )
            }
            else if (colType === 'User' || colType === 'UserMulti') {
              newColumn.onRender = (item) => {
                if (listCol.UserDisplayOptions === "NamePhoto") {
                  return <div>
                    <div style={pillContainer}>
                      {Array.isArray(item[colName]) && item[colName].map(item => (
                        <div key={item.id} style={peoplePill}>
                          <Persona styles={peoplePillPersonaStyles} text={item.value || item.title} size={PersonaSize.size24} showOverflowTooltip={false} />
                        </div>
                      ))}
                    </div>
                  </div>
                }

                return <div style={gridColumnMultiValueContainer}>
                  {Array.isArray(item[colName]) && item[colName].map(item => (
                    <div key={item.id} style={gridRowMultiValueStyle}>
                      <span>{item.value || item.title}</span>
                    </div>
                  ))}
                </div>
              }
            }
            else if (colType === 'Note') {
              newColumn.minWidth = 150;
              newColumn.onRender = item => {
                const lines = splitStringByNewline(item[colName]);
                return <span style={noteGridCellStyle}>{lines.join("\r\n")}</span>
              }
            }
            else if (colType === "Thumbnail") {
              newColumn.onRender = item => {
                const thumbData = item[colName];
                if (thumbData?.thumbnailRenderer && thumbData.thumbnailRenderer.spItemUrl) {
                  return <ImageRenderer imageName={thumbData.fileName} imageItemUrl={thumbData.thumbnailRenderer.spItemUrl} />
                }
                else if (thumbData?.fileName) {
                  return <ImageRenderer imageName={thumbData.originalImageName} imageItemUrl={`${currentDirectory.webUrl}/_api/v2.1/sites('${currentDirectory.siteId}')/lists('${currentDirectory.sharePointListId}')/items('${item.ID}')/attachments('${encodeURIComponent(thumbData.fileName)}')`} />
                }
                return <></>;
              }
            }
            else if (colType === "Boolean") {
              const booleanCellContentStyle: React.CSSProperties = {
                display: "flex",
                fontSize: 14,
                justifyContent: "center",
                width: `${colName.length}ch`
              };
              newColumn.onRender = item => {
                return <span style={booleanCellContentStyle}>{item[`${colName}.value`] === "1" && "✓"}</span>
              }
            }
            else {
              newColumn.onRender = item => (
                <span style={textGridCellStyle}>{item[colName]}</span>
              )
            }
            newCols.push(newColumn);
          }
        });

        newGridProps.columns = newCols;

        setGridProps(newGridProps);

        // reloadGrid({ ...newGridProps });
        dispatch(toggleContentManagerReload());
      } catch (error) {
        logger(error, "useContentManagerBody.tsx (Selected view column init)");
      }
    }
  }, [selectedView]);

  const onFolderClick = React.useCallback(async (item: any) => {
    const isInList = currentDirectory.type === ComponentTypes.SharePointList;
    let itemid = "";
    // let sharepointId = item.UniqueId.replace('{', '').replace('}', '');
    let driveId = isInOneDrive.current ? oneDriveRootDetails.driveId : (currentDirectory.parentId ? currentDirectory.parentId : currentDirectory.id);

    const spItemUrlparts = item['.spItemUrl']?.split('/');
    if (spItemUrlparts) {
      itemid = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
    }

    let fetchDocumentSetSettings: boolean = false;
    let documentSetContentTypeId: string;
    if (!isInOneDrive.current) {
      if (item.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
        documentSetContentTypeId = item.ContentTypeId;
      }
      else {
        documentSetContentTypeId =
          currentDirectory?.parentDocumentSetContentTypeId
          ??
          workingDirectories?.find(dir => dir?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet))?.contentTypeId;
      }

      if (documentSetContentTypeId && !(documentSetDefaultViews.current?.findIndex(docSet => docSet.contentTypeId === documentSetContentTypeId) > -1)) {
        fetchDocumentSetSettings = true;
      }
    }

    if (fetchDocumentSetSettings || !itemid) {
      setIsFetchingData(true);
      if (fetchDocumentSetSettings) {
        const documentSetSettings = await SharepointContentTypeHelper.getDocumentSetSettings(await authModule.getSharePointAccessToken(), currentDirectory.webUrl, currentDirectory.sharePointListId, documentSetContentTypeId)
        if (documentSetSettings) {
          const docSetDefaultViews: IDocumentSetDefaultView[] = [...documentSetDefaultViews.current];
          docSetDefaultViews.push({ contentTypeId: documentSetContentTypeId, viewId: documentSetSettings.viewId });
          documentSetDefaultViews.current = docSetDefaultViews;
        }
      }
      if (!itemid && !isInList) {
        const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
        const folderUrl = new URL(driveUrl.origin + item.FileRef);
        itemid = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
      }
      setIsFetchingData(false);
    }

    const siteUrlPath: string = new URL(currentDirectory.webUrl)?.pathname + "/";

    const itemToAdd: IPageSettings = {
      id: itemid,
      parentId: driveId,
      type: isInOneDrive.current ? ComponentTypes.OneDrive : ComponentTypes.SharePointFolders,
      siteId: currentDirectory.siteId,
      siteDisplayName: currentDirectory.siteDisplayName,
      webUrl: currentDirectory.webUrl,
      fileTitle: item.FileLeafRef,
      directory: item.FileRef.replace(siteUrlPath, ""),
      DocumentLibrary: currentDirectory.DocumentLibrary,
      displayName: item.FileLeafRef,
      isTeamsFolder: currentDirectory.isTeamsFolder,
      sharePointListId: currentDirectory.sharePointListId,
      contentTypeId: item.ContentTypeId,
      serverRelativePath: item.FileRef,
      sharePointUniqueId: item.UniqueId?.replace("{", "").replace("}", "")
    };

    dispatch(setContentManagerFilterSections([]));
    dispatch(setContentManagerFilterFields([]));
    dispatch(addWorkingDirectory(itemToAdd));
    dispatch(toggleContentManagerReload());
  }, [currentDirectory, oneDriveRootDetails, workingDirectories]);

  const checkMissingMetadata = React.useCallback((item: any): boolean => {
    if (item?.ContentTypeId) {
      const itemContentType: any = contentTypes?.find(ct => ct.Id.StringValue === item.ContentTypeId);
      if (Array.isArray(itemContentType?.RequiredFields) && itemContentType.RequiredFields.length > 0) {
        try {
          for (let i = 0; i < itemContentType.RequiredFields.length; i++) {
            const propName: string = itemContentType.RequiredFields[i].InternalName;
            const propValue = item[propName];
            if (typeof propValue === "undefined"
              || propValue === null
              || (Array.isArray(propValue) && propValue.length === 0)
              || !propValue
            ) {
              return true;
            }
          }
        } catch (error) {
          logger(error, "useContentManagerBody.tsx (checkMissingMetadata)");
        }
      }
    }
    return false;
  }, [contentTypes]);

  const getTimeZoneFormattedDate = (dateString: string, format: string): string => {
    if (siteLocaleInfoRef.current !== null) {
      try {
        const currentLocale: string = dayjs.locale();
        return dayjs(dateString).tz(siteLocaleInfoRef.current.timeZoneName).locale(currentLocale === "en" ? siteLocaleInfoRef.current.locale : currentLocale).format(format);
      } catch (error) {
        logger(error, "useContentManagerBody.tsx (getTimeZoneFormattedDate)");
      }
    }
    return dayjs.utc(dateString).local().format(format);
  }

  const addRow = async (itemId: string) => {
    try {
      let listStreamByID = await renderListAsStreamByID({
        siteUrl: isInOneDrive?.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
        sharePointListId: currentDirectory?.sharePointListId,
        viewId: selectedViewId.current,
        accessToken: isInOneDrive?.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
        Id: itemId,
        serverRelativePath: serverRelativePathRef.current
      }).unwrap();

      if (listStreamByID.ListData?.Row?.length === 1) {
        setGridProps(prevGridProps => {
          const updatedItems = [];
          if (prevGridProps.items.findIndex(item => item && item.ID === listStreamByID.ListData.Row[0].ID) === -1) {
            updatedItems.push(listStreamByID.ListData.Row[0]);
            prevGridProps.items.forEach(item => {
              updatedItems.push(item);
            });
          }
          else {
            prevGridProps.items.forEach(item => {
              if (item && item.ID === listStreamByID.ListData.Row[0].ID) {
                updatedItems.push(listStreamByID.ListData.Row[0]);
              } else {
                updatedItems.push(item);
              }
            });
          }

          const currentSelectedItems = gridSelection.getSelection();
          if (currentSelectedItems?.length > 0) {
            updateGridSelection(currentSelectedItems, updatedItems);
          }

          return { ...prevGridProps, items: updatedItems };
        });
      }
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (addRow)");
    }
  }

  const reloadRow = async (itemId: string) => {
    try {
      let listStreamByID = await renderListAsStreamByID({
        siteUrl: isInOneDrive?.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
        sharePointListId: currentDirectory?.sharePointListId,
        viewId: selectedViewId.current,
        accessToken: isInOneDrive?.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
        Id: itemId,
        serverRelativePath: serverRelativePathRef.current
      }).unwrap();

      if (listStreamByID.ListData?.Row?.length === 1) {
        setGridProps(prevGridProps => {
          if (prevGridProps.items.findIndex(item => item && item.ID === listStreamByID.ListData.Row[0].ID) !== -1) {
            const newRows = [];
            prevGridProps.items.forEach(item => {
              if (item && item.ID === listStreamByID.ListData.Row[0].ID) {
                newRows.push(listStreamByID.ListData.Row[0]);
              } else {
                newRows.push(item);
              }
            });
            return { ...prevGridProps, items: newRows };
          }
          return prevGridProps;
        });
      }
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (reloadRow)");
    }
  }

  const removeRow = (itemId: string) => {
    try {
      setGridProps(prevGridProps => {
        const updatedItems = prevGridProps.items.filter(item => item?.ID !== itemId);

        const currentSelectedItems = gridSelection.getSelection();
        if (currentSelectedItems?.length > 0) {
          updateGridSelection(currentSelectedItems, updatedItems);
        }

        return { ...prevGridProps, items: updatedItems };
      });
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (removeRow)");
    }
  }

  const lockRow = async (itemId: string) => {
    try {
      setGridProps(prevGridProps => {
        const newRows = [];
        prevGridProps.items.forEach(item => {
          if (item && item.ID === itemId) {
            newRows.push({ ...item, [rowLockIndicator]: true });
          } else {
            newRows.push(item);
          }
        });
        return { ...prevGridProps, items: newRows };
      });
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (lockRow)");
    }
  }

  const updateGridSelection = React.useCallback((currentSelectedItems: IObjectWithKey[], updatedItems: any[]) => {
    gridSelection.setChangeEvents(false);

    const idToIndexMap = new Map();
    updatedItems.forEach((row, index) => {
      if (row && row["ID"]) {
        idToIndexMap.set(row["ID"], index);
      }
    });

    gridSelection.setItems(updatedItems, true);

    currentSelectedItems.forEach(selectedItem => {
      if (selectedItem) {
        const newIndex = idToIndexMap.get(selectedItem["ID"]);
        if (newIndex !== undefined) {
          gridSelection.setIndexSelected(newIndex, true, false);
        }
      }
    });

    gridSelection.setChangeEvents(true);
  }, [gridSelection]);

  const initGridLoad = (gridData?: GridProps): boolean => {
    if (!isInOneDrive.current && !gridData && currentDirectory?.id !== selectedViewDirectoryId.current) {
      let updatedViewId: string = directoryViewModule.getDirectoryView(currentDirectory?.id)?.viewId;
      if (updatedViewId && rawViews && !(rawViews.findIndex(view => view.Id === updatedViewId) > -1)) {
        directoryViewModule.removeDirectoryView(updatedViewId);
        updatedViewId = null;
      }

      let documentSetContentTypeId: string;
      if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
        documentSetContentTypeId = currentDirectory.contentTypeId;
      }
      else {
        documentSetContentTypeId =
          currentDirectory?.parentDocumentSetContentTypeId
          ??
          workingDirectories?.find(dir => dir?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet))?.contentTypeId;

        if (!updatedViewId && currentDirectory?.contentTypeId) {
          const contentTypeName: string = contentTypes?.find(contentType => contentType.Id?.StringValue === currentDirectory.contentTypeId)?.Name;
          if (contentTypeName) {
            updatedViewId = rawViews?.find(view => view.Title === contentTypeName)?.Id;
          }
        }
      }

      if (!isInDocumentSet.current && documentSetContentTypeId) {
        isInDocumentSet.current = true;
        if (!updatedViewId) {
          updatedViewId = documentSetDefaultViews.current?.find(docSet => docSet.contentTypeId === documentSetContentTypeId)?.viewId;
        }
      }
      else if (isInDocumentSet.current && !documentSetContentTypeId) {
        isInDocumentSet.current = false;
        if (!updatedViewId) {
          updatedViewId = rawViews?.find(view => view.DefaultView === true)?.Id;
        }
      }

      if (updatedViewId && updatedViewId !== selectedViewId.current) {
        const updatedView: IViewsDropdown = viewsWithColumns?.find(view => view.Id === updatedViewId);
        if (updatedView) {
          setSelectedView(updatedView);
          return undefined;
        }
      }
    }

    if ((gridData ?? gridProps)?.columns?.length > 0) {
      return true;
    }
    return false;
  }

  const fetchListStream = async (
    gridData?: GridProps
  ): Promise<{ requestId: number, requestCurrentDirectoryId: string, requestViewId: string, serverRelativePath: string, listStreamData: any, gridData: GridProps }> => {
    let requestId = 0;
    let requestCurrentDirectoryId = "";
    let requestViewId = "";

    try {
      const newGridProps: GridProps = gridData ? { ...gridData } : { ...gridProps };
      setIsFetchingData(true);

      requestId = ++activeListRequestId.current;
      requestCurrentDirectoryId = currentDirectory?.id ?? "";
      requestViewId = selectedViewId.current ?? "";

      let serverRelativePath = '';
      if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) {
        serverRelativePath = currentDirectory?.serverRelativePath;
      }
      else if (currentDirectory?.directory?.length > 0 && currentDirectory?.parentId) {
        let driveId = isInOneDrive.current ? oneDriveRootDetails.driveId : (currentDirectory.parentId ? currentDirectory.parentId : currentDirectory.id);
        const results = await getItemWebUrl({ driveId, itemId: currentDirectory?.id, accessToken: await authModule.getGraphAccessToken() }).unwrap();
        const resolvedPath = tryGetPathname(results.listItem?.webUrl || results.webUrl);
        serverRelativePath = resolvedPath ? tryDecodeUriComponent(resolvedPath) : currentDirectory?.serverRelativePath ?? "";
        if (serverRelativePath.includes('.aspx')) {
          serverRelativePath = currentDirectory?.serverRelativePath;
        }
      }

      let listStream;

      let viewId = "";
      let viewXml = "";

      if (selectedView?.isReport) {
        const defaultView = rawViews?.find(x => x.DefaultView === true);
        if (defaultView) {
          viewId = defaultView.Id;
          viewXml = defaultView.ListViewXml;
        }
      } else {
        viewId = selectedView?.Id;
        viewXml = !selectedView ? rawViews?.find(x => x.DefaultView === true).ListViewXml : rawViews?.find(x => x.Title === selectedView?.text).ListViewXml;
      }

      try {
        listStream = await getListAsStream({
          siteUrl: isInOneDrive?.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
          sharePointListId: currentDirectory?.sharePointListId,
          viewId,
          viewXml,
          gridProps: { ...newGridProps },
          accessToken: isInOneDrive?.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          serverRelativePath,
          filterSections,
          searchValue,
          isReport: selectedView?.isReport
        }).unwrap();
      } catch (error) {
        // SPQueryThrottledException
        let isThrottled: boolean = false;
        try {
          if (error?.data && error.data["odata.error"]?.code?.startsWith("-2147024860")) {
            isThrottled = true;
          }
        } catch (e) {
          if (error && JSON.stringify(error)?.indexOf("-2147024860") > -1) {
            isThrottled = true;
          }
        }

        if (isThrottled) {
          listStream = await getListAsStream({
            siteUrl: isInOneDrive?.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
            sharePointListId: currentDirectory?.sharePointListId,
            viewId,
            viewXml,
            gridProps: { ...newGridProps },
            accessToken: isInOneDrive?.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
            serverRelativePath,
            filterSections,
            searchValue,
            includeReplaceGroup: true,
            isReport: selectedView?.isReport
          }).unwrap();
        }
      }

      if (listStream) {
        return { requestId, requestCurrentDirectoryId, requestViewId, serverRelativePath, listStreamData: listStream, gridData: newGridProps };
      }
      else if (isActiveListRequest(requestId, requestCurrentDirectoryId, requestViewId)) {
        setIsFetchingData(false);
      }
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (fetchListStream)");
      if (!requestId || isActiveListRequest(requestId, requestCurrentDirectoryId, requestViewId)) {
        setIsFetchingData(false);
      }
    }
    return undefined;
  }

  const processListStreamResult = async (
    gridData: GridProps,
    serverRelativePath: string,
    listStream: any,
    requestState?: { requestId: number, requestCurrentDirectoryId: string, requestViewId: string }
  ) => {
    try {
      const requestId = requestState?.requestId ?? activeListRequestId.current;
      const requestCurrentDirectoryId = requestState?.requestCurrentDirectoryId ?? currentDirectory?.id;
      const requestViewId = requestState?.requestViewId ?? selectedViewId.current;

      if (!isActiveListRequest(requestId, requestCurrentDirectoryId, requestViewId)) {
        return;
      }

      setLibraryMinVerEnabled(listStream.EnableMinorVersions ? listStream.EnableMinorVersions : false);

      listHttpRoot.current = listStream.HttpRoot;

      if (!serverRelativePath) {
        serverRelativePath = listStream.ListUrl;
      }

      setFolderPath(serverRelativePath.replace(listStream.ListUrl, ''));

      serverRelativePathRef.current = serverRelativePath;
      setServerRelPath(serverRelativePath);
      setListTitle(listStream.ListTitle);

      if (listStream.ListData.Row?.length > 0) {
        dispatch(setContentManagerFilterFields(listStream.ListSchema?.Field));
      } else {
        dispatch(setContentManagerFilterFields([]));
      }

      dispatch(setCanViewFolder(buildCurrentSharepointFolderState({
        existingFolderState: currentSharepointFolderRef.current,
        folderUpdate: {
          sharepointListId: currentDirectory?.sharePointListId || listStream.listName?.replace("{", "").replace("}", ""),
          sharePointUniqueId: currentResolvedSharePointUniqueIdRef.current ?? currentDirectory?.sharePointUniqueId,
          canCreateFolder: listStream.AllowCreateFolder,
          contentTypesEnabled: listStream.ContentTypesEnabled,
          enableMinorVersions: listStream.EnableMinorVersions,
          disableOpenInApp: listStream.ExcludeFromOfflineClient === true,
          forceCheckout: listStream.isForceCheckout == true,
        },
        directoryIdentity: currentDirectoryPermissionIdentityRef.current,
        directoryRequestKey: currentDirectoryRequestKeyRef.current,
        permissionState: currentFolderPermissionStateRef.current
      })));

      if (gridData.groupProps) { //Sharepoint Grouping applied
        if (gridData.groupProps.collapse) {
          //Groups are collapsed     
          buildGroupFromTableData(gridData, listStream.ListData.Row as any[], true);
          if (listStream.ListData.Row.length === 0) {
            gridData.items = [];
          }
          setGridProps({ ...gridData });
          setIsFetchingData(false);

          let nextPageUrl: string = listStream.ListData.NextHref ? listStream.ListData.NextHref : '';

          while (nextPageUrl) {
            const listStreamNextPage = await getListAsStreamNextPage({
              siteUrl: currentDirectory.webUrl,
              sharePointListId: currentDirectory.sharePointListId,
              nextPageUrl,
              accessToken: isInOneDrive?.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
              filterSections,
              searchValue
            }).unwrap();

            if (!isActiveListRequest(requestId, requestCurrentDirectoryId, requestViewId)) {
              return;
            }

            buildGroupFromTableData(gridData, listStreamNextPage.ListData.Row as any[], false);
            setGridProps({ ...gridData });

            nextPageUrl = listStreamNextPage.ListData.NextHref ? listStreamNextPage.ListData.NextHref : '';
          }

        }
        else {
          //Groups are expanded         
          let nextPageUrl: string = '';
          let responseViewId = listStream.view.replace('{', '').replace('}', '').toLowerCase();

          if (isActiveListRequest(requestId, requestCurrentDirectoryId, requestViewId) && (!selectedViewId.current || responseViewId === selectedViewId.current)) {
            gridData.items = [];
            gridData.groups = [];

            const groupItems: any[] = searchValue?.value?.trim().length > 0 && CacheHelper.get(DocubirdCache.isStrictSearch)
              ? filterStrictSearchResults(isSharePointList, listStream.ListData.Row, searchValue.value)
              : listStream.ListData.Row;

            buildExpandedGroupsFromData(gridData, [...groupItems], false);
            setGridProps({ ...gridData });

            nextPageUrl = listStream.ListData.NextHref ? listStream.ListData.NextHref : '';
          }

          setIsFetchingData(false);

          let count = 1;
          while (nextPageUrl) {
            if (isActiveListRequest(requestId, requestCurrentDirectoryId, requestViewId) && nextPageUrl.includes(selectedViewId.current)) {
              const listStreamExpandedNextPage = await getListAsStreamNextPage({
                siteUrl: currentDirectory.webUrl,
                sharePointListId: currentDirectory.sharePointListId,
                nextPageUrl,
                accessToken: isInOneDrive?.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                filterSections,
                searchValue
              }).unwrap();

              responseViewId = listStreamExpandedNextPage.view.replace('{', '').replace('}', '').toLowerCase();

              if (isActiveListRequest(requestId, requestCurrentDirectoryId, requestViewId) && responseViewId === selectedViewId.current) {

                const nextPageItems: any[] = searchValue?.value?.trim().length > 0 && CacheHelper.get(DocubirdCache.isStrictSearch)
                  ? filterStrictSearchResults(isSharePointList, listStreamExpandedNextPage.ListData.Row, searchValue.value)
                  : listStreamExpandedNextPage.ListData.Row;

                buildExpandedGroupsFromData(gridData, [...nextPageItems], true)
                nextPageUrl = listStreamExpandedNextPage.ListData.NextHref ? listStreamExpandedNextPage.ListData.NextHref : '';

                (count % 3 === 0 || !nextPageUrl) && setGridProps({ ...gridData });
              }
              else {
                nextPageUrl = '';
              }

              count += 1;
            } else {
              nextPageUrl = '';
            }
          }
        }
      }
      else {
        //No groupings
        gridData.nextUrl = listStream.ListData.NextHref;

        const viewItems: any[] = searchValue?.value?.trim().length > 0 && CacheHelper.get(DocubirdCache.isStrictSearch)
          ? filterStrictSearchResults(isSharePointList, listStream.ListData.Row as any[], searchValue.value)
          : listStream.ListData.Row as any[];

        gridData.items = [...viewItems];
        gridData.hasMoreResults = listStream.ListData.NextHref ? true : false;

        if (gridData.hasMoreResults) {
          if (selectedView?.viewType === ViewTypes.TILES) {
            fetchListStreamNextPage(gridData);
          }
          else {
            gridData.items.push(null);
          }
        }

        setGridProps({ ...gridData });
        setIsFetchingData(false);
        setScrollToTopToggle(prev => !prev);
      }
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (processListStreamResult)");
      if (!requestState || isActiveListRequest(requestState.requestId, requestState.requestCurrentDirectoryId, requestState.requestViewId)) {
        setIsFetchingData(false);
      }
    }
  }

  const reloadGrid = async (gridData?: GridProps) => {
    const loadData: boolean = initGridLoad(gridData);
    if (loadData) {
      const listStreamResult = await fetchListStream(gridData);
      if (listStreamResult) {
        processListStreamResult(listStreamResult.gridData, listStreamResult.serverRelativePath, listStreamResult.listStreamData, listStreamResult);
      }
    }
  }

  const getNewDocumentTemplates = (newDocTemplates: string, contentTypes: any[]) => {
    const defaultTemplates: NewDocumentTemplate[] = [
      { templateId: "NewFolder", title: languageStrings.Folder, visible: true },
      { templateId: 'NewDOC', title: 'Word document', visible: true, extention: 'docx', documentTemplateType: DocumentTemplateTypes.docx },
      { templateId: 'NewXSL', title: 'Excel workbook', visible: true, extention: 'xlsx', documentTemplateType: DocumentTemplateTypes.xlsx },
      { templateId: 'NewPPT', title: 'PowerPoint presentation', visible: true, extention: 'pptx', documentTemplateType: DocumentTemplateTypes.pptx },
      { templateId: 'NewONE', title: 'OneNote notebook', visible: true, extention: 'onetoc', documentTemplateType: DocumentTemplateTypes.one },
      { templateId: 'NewXSLSurvey', title: 'Excel survey', visible: false, extention: 'xlsx' },
      { templateId: 'NewXSLForm', title: 'Forms for Excel', visible: false, extention: 'xlsx' },
      { templateId: 'NewVSDX', title: 'Visio drawing', visible: true, extention: 'vsdx', documentTemplateType: DocumentTemplateTypes.vsdx },
      { templateId: "NewLink", title: languageStrings.Link, visible: true }
    ];

    const documentSetContentTypes = Array.isArray(contentTypes) && contentTypes.length > 0 && contentTypes.filter(x => x.Id?.StringValue?.startsWith(SharePointFolderContentTypeIds.documentSet));
    if (Array.isArray(documentSetContentTypes) && documentSetContentTypes.length > 0) {
      const documentSetTemplates = documentSetContentTypes.map(contentType => {
        const newDocSetTemplate: NewDocumentTemplate = {
          templateId: contentType.Id?.StringValue,
          title: contentType.Name,
          visible: true,
          isContentType: true,
          contentTypeId: contentType.Id?.StringValue
        }
        return newDocSetTemplate;
      });

      defaultTemplates.splice(1, 0, ...documentSetTemplates);
    }

    if (newDocTemplates) {
      try {
        const templates: NewDocumentTemplate[] = JSON.parse(newDocTemplates);

        setNewDocumentTemplates(templates
          .map(template => {
            const selectedTemplate = defaultTemplates.find(x => x.templateId === template.templateId);
            if (selectedTemplate) {
              return {
                ...selectedTemplate,
                ...template
              }
            } else if (template.url) {
              const extention = getExtension(template.url);
              const documentTemplateType = DocumentTemplateTypes[extention];
              if (documentTemplateType) {
                return {
                  ...template,
                  extention,
                  documentTemplateType
                }
              }
            }
            return null;
          })
          .filter(template => template !== null && (template.visible === undefined || template.visible === true))
          .concat({ templateId: "NewLink", title: languageStrings.Link, visible: true })
        );
      } catch (error) {
        logger(error, "useContentManagerBody.tsx (getNewDocumentTemplates)");
        setNewDocumentTemplates(defaultTemplates);
      }
    } else {
      setNewDocumentTemplates(defaultTemplates);
    }
  }

  const fetchListStreamNextPage = async (gridPropsParam?: GridProps) => {
    if (!isFetchingNextPage.current) {
      try {
        if ((gridPropsParam ?? gridProps)?.nextUrl) {
          isFetchingNextPage.current = true;

          const newGridProps: GridProps = { ...(gridPropsParam ?? gridProps) };

          let fetchListStreamNextPage = await getListAsStreamNextPage({
            siteUrl: isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory.webUrl,
            sharePointListId: currentDirectory.sharePointListId,
            nextPageUrl: newGridProps.nextUrl,
            accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
            filterSections,
            searchValue
          }).unwrap();

          let updatedItems = [...newGridProps.items];

          if (updatedItems.length > 0 && !updatedItems[updatedItems.length - 1]) {
            updatedItems.pop();
          }

          const nextPageItems: any[] = searchValue?.value?.trim().length > 0 && CacheHelper.get(DocubirdCache.isStrictSearch)
            ? filterStrictSearchResults(isSharePointList, fetchListStreamNextPage.ListData.Row, searchValue.value)
            : fetchListStreamNextPage.ListData.Row as any[];

          newGridProps.items = updatedItems.concat(nextPageItems);
          newGridProps.nextUrl = fetchListStreamNextPage.ListData?.NextHref;
          newGridProps.hasMoreResults = fetchListStreamNextPage.ListData?.NextHref ? true : false;

          if (newGridProps.hasMoreResults) {
            newGridProps.items.push(null);
          }

          isFetchingNextPage.current = false;
          setGridProps({ ...newGridProps });
        }
      } catch (error) {
        logger(error, "useContentManagerBody.tsx (fetchListStreamNextPage)");
      }
    }
  }

  const buildGroupFromTableData = (newGridProps: GridProps, groupRows: any[], resetGroups: boolean) => {
    try {
      const groups: IGridGroup[] = resetGroups ? [] : newGridProps.groups;
      const groupingCount = newGridProps.groupProps.names.length;

      groupRows?.forEach((row, index) => {
        if (groupingCount === 1) {
          const groupName = newGridProps.groupProps.names[0];
          let groupNameValue = getGroupNameValue(groupName, row);
          let group1NameDisplay = getGroupDisplayName(groupName);

          let parentGroup: IGroup = groups.find(x => x.key === `g1_${groupNameValue.toLowerCase()}`);

          if (!parentGroup) {
            const newGroup: IGridGroup = {
              key: `g1_${groupNameValue.toLowerCase()}`,
              name: `${group1NameDisplay}: ${groupNameValue} (${row[`${groupName}.COUNT.group`]})`,
              startIndex: 0,
              count: 0,
              totalCount: +row[`${groupName}.COUNT.group`],
              level: 0,
              isCollapsed: true,
              urlencoded: row[`${groupName}.urlencoded`],
              filterField: groupName,
              // filterValue: row[`${groupName}.singleurlencoded`] ? row[`${groupName}.singleurlencoded`] : row[`${groupName}.urlencoded`]
              filterValue: row[`${groupName}.singleurlencoded`] ?? ""
            }

            groups.push(newGroup)
          }
        }
        else if (groupingCount === 2) {
          const group1Name = newGridProps.groupProps.names[0];
          const group2Name = newGridProps.groupProps.names[1];

          const group1NameValue = getGroupNameValue(group1Name, row);
          const group2NameValue = getGroupNameValue(group2Name, row);

          const group1NameDisplay = getGroupDisplayName(group1Name);
          const group2NameDisplay = getGroupDisplayName(group2Name);

          let parentGroup: IGroup = groups.find(x => x.key === `g1_${group1NameValue}`);

          if (!parentGroup) {
            const childGroup: IGridGroup = {
              key: `g2_${group2NameValue}`,
              name: `${group2NameDisplay}: ${group2NameValue} (${row[`${group2Name}.COUNT.group2`]})`,
              startIndex: 0,
              count: 0,
              totalCount: +row[`${group2Name}.COUNT.group`],
              level: 1,
              isCollapsed: true,
              urlencoded: row[`${group2Name}.urlencoded`],
              filterField: group2Name,
              // filterValue: row[`${group2Name}.singleurlencoded`] ? row[`${group2Name}.singleurlencoded`] : row[`${group2Name}.urlencoded`],
              filterValue: row[`${group2Name}.singleurlencoded`] ?? "",
              parentKey: `g1_${group1NameValue}`
            }

            const newParentGroup: IGridGroup = {
              key: `g1_${group1NameValue}`,
              name: `${group1NameDisplay}: ${group1NameValue} (${row[`${group1Name}.COUNT.group`]})`,
              startIndex: 0,
              count: 0,
              totalCount: +row[`${group1Name}.COUNT.group`],
              level: 0,
              isCollapsed: true,
              urlencoded: row[`${group1Name}.urlencoded`],
              filterField: group1Name,
              // filterValue: row[`${group1Name}.singleurlencoded`] ? row[`${group1Name}.singleurlencoded`] : row[`${group1Name}.urlencoded`],
              filterValue: row[`${group1Name}.singleurlencoded`] ?? "",
              children: [childGroup]
            }

            groups.push(newParentGroup);
          } else {
            //const parentGroup = groups.find(x => x.key === `g1_${row[`${group1Name}.groupindex`]}`);
            let childGroup: IGroup = parentGroup.children?.find(x => x.key === `g2_${group2NameValue}`);

            if (!childGroup) {
              const newChildGroup: IGridGroup = {
                key: `g2_${group2NameValue}`,
                name: `${group2NameDisplay}: ${group2NameValue} (${row[`${group2Name}.COUNT.group2`]})`,
                startIndex: 0,
                count: 0,
                totalCount: +row[`${group2Name}.COUNT.group`],
                level: 1,
                isCollapsed: true,
                urlencoded: row[`${group2Name}.urlencoded`],
                filterField: group2Name,
                // filterValue: row[`${group2Name}.singleurlencoded`] ? row[`${group2Name}.singleurlencoded`] : row[`${group2Name}.urlencoded`],
                filterValue: row[`${group2Name}.singleurlencoded`] ?? "",
                parentKey: `g1_${group1NameValue}`
              }

              parentGroup.children.push(newChildGroup);
            }
          }
        }
      })

      newGridProps.groups = [...groups];
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (buildGroupFromTableData)");
    }
  }

  const buildExpandedGroupsFromData = (gridProps: GridProps, newItems: any[], isPagedResults: boolean = false, resestGroups: boolean = false, limitToOneGrouping: boolean = false) => {
    let groups: IGroup[] = [...gridProps.groups];

    if (resestGroups === false) {
      groups = gridProps.groups ? [...gridProps.groups] : [];
    }

    const groupingCount = gridProps.groupProps.names.length;

    if (groupingCount === 1) {
      const groupName = gridProps.groupProps.names[0];

      newItems.forEach((row, index) => {
        const groupNameValue = getGroupNameValue(groupName, row);
        const groupNameDisplay = getGroupDisplayName(groupName);
        let parentGroup: IGroup = groups.find(x => x.key === `g1_${groupNameValue.toLowerCase()}`);

        if (!parentGroup) {
          const newGroup: IGridGroup = {
            key: `g1_${groupNameValue.toLowerCase()}`,
            name: `${groupNameDisplay}: ${groupNameValue} (${row[`${groupName}.COUNT.group`]})`,
            startIndex: gridProps.items.length + index,
            totalCount: +row[`${groupName}.COUNT.group`],
            count: 1,
            level: 0,
            isCollapsed: false,
            urlencoded: row[`${groupName}.urlencoded`]
          }

          groups.push(newGroup)
        }
        else {
          parentGroup.count = parentGroup.count + 1;
        }
      });
    }
    else if (groupingCount === 2) {
      const groupName = gridProps.groupProps.names[0];
      const groupName2 = gridProps.groupProps.names[1];

      newItems.forEach((row, index) => {
        const groupNameValue = getGroupNameValue(groupName, row);
        const groupName2Value = getGroupNameValue(groupName2, row);

        const groupNameDisplay = getGroupDisplayName(groupName);
        const groupName2Display = getGroupDisplayName(groupName2);

        let parentGroup: IGroup = groups.find(x => x.key === `g1_${groupNameValue.toLowerCase()}`);

        if (!parentGroup) {
          const newChildGroup: IGridGroup = {
            key: `g2_${groupName2Value.toLowerCase()}`,
            name: `${groupName2Display}: ${groupName2Value} (${row[`${groupName2}.COUNT.group2`]})`,
            startIndex: gridProps.items.length + index,
            totalCount: +row[`${groupName2}.COUNT.group`],
            count: 1,
            level: 1,
            isCollapsed: false,
            urlencoded: row[`${groupName2}.urlencoded`],
          }

          const newParentGroup: IGridGroup = {
            key: `g1_${groupNameValue.toLowerCase()}`,
            name: `${groupNameDisplay}: ${groupNameValue} (${row[`${groupName}.COUNT.group`]})`,
            startIndex: 0,
            totalCount: +row[`${groupName}.COUNT.group`],
            count: 0,
            level: 0,
            isCollapsed: false,
            children: [newChildGroup],
            urlencoded: row[`${groupName}.urlencoded`]
          }

          groups.push(newParentGroup)
        }
        else {
          let childGroup: IGroup = parentGroup.children?.find(x => x.key === `g2_${groupName2Value.toLowerCase()}`);
          if (!childGroup) {
            const newChildGroup: IGridGroup = {
              key: `g2_${groupName2Value.toLowerCase()}`,
              name: `${groupName2Display}: ${groupName2Value} (${row[`${groupName2}.COUNT.group2`]})`,
              startIndex: gridProps.items.length + index,
              totalCount: +row[`${groupName2}.COUNT.group`],
              count: 1,
              level: 1,
              isCollapsed: false,
              urlencoded: row[`${groupName2}.urlencoded`],
            }
            parentGroup.children?.push(newChildGroup)
          }
          else {
            childGroup.count = childGroup.count + 1;
          }
        }
      });
    }

    gridProps.items = [...gridProps.items].concat(newItems);
    gridProps.groups = [...groups];
  }

  const fetchGroupedDrillDownData = async (group: IGridGroup) => {
    const groupingCount = gridProps.groupProps.names.length;

    try {
      if (gridProps.groupProps.collapse) {
        const newGridProps = { ...gridProps }
        const sortProps = [...gridProps.sort];

        let serverRelativePath = '';
        if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) {
          serverRelativePath = currentDirectory.serverRelativePath
        }
        else if (currentDirectory?.directory?.length > 0 && currentDirectory?.parentId) {
          let driveId = isInOneDrive.current ? oneDriveRootDetails.driveId : (currentDirectory.parentId ? currentDirectory.parentId : currentDirectory.id);
          const results = await getItemWebUrl({ driveId, itemId: currentDirectory?.id, accessToken: await authModule.getGraphAccessToken() }).unwrap();
          const resolvedPath = tryGetPathname(results.listItem?.webUrl || results.webUrl);
          serverRelativePath = resolvedPath ? tryDecodeUriComponent(resolvedPath) : currentDirectory?.serverRelativePath ?? "";
        }

        let nextHref = '';

        if (groupingCount === 1) {
          do {
            const listStreamDrilldown = await getListAsStreamGroupDrillDown({
              siteUrl: isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory.webUrl,
              sharePointListId: currentDirectory.sharePointListId,
              viewId: selectedView.Id,
              groupString: group.urlencoded,
              sortProps: sortProps,
              accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
              serverRelativePath,
              filterField: group.filterField,
              filterValue: group.filterValue,
              nextHref,
              filterSections,
              searchValue
            }).unwrap();

            const groupItems: any[] = searchValue?.value?.trim().length > 0 && CacheHelper.get(DocubirdCache.isStrictSearch)
              ? filterStrictSearchResults(isSharePointList, listStreamDrilldown.ListData.Row, searchValue.value)
              : listStreamDrilldown.ListData.Row as any[];

            newGridProps.items = newGridProps.items ? [...newGridProps.items].concat(groupItems) : [...groupItems];

            nextHref = listStreamDrilldown.ListData.NextHref ? listStreamDrilldown.ListData.NextHref : '';

            let currentGroup: IGroup = null;

            if (!group.parentKey) {
              currentGroup = newGridProps.groups.find(x => x.key === group.key);
            }
            else {
              const parentGroup = newGridProps.groups.find(x => x.key === group.parentKey);
              if (parentGroup) {
                currentGroup = parentGroup.children.find(x => x.key === group.key);
              }
            }

            if (currentGroup) {
              const allItemsCount = newGridProps.items?.length > 0 ? newGridProps.items?.length : 0;
              const newItemsCount = groupItems.length;

              currentGroup.startIndex = currentGroup.count === 0 ? allItemsCount - newItemsCount : currentGroup.startIndex
              currentGroup.count = currentGroup.count + newItemsCount;
            }

            setGridProps({ ...newGridProps, items: [...newGridProps.items] });

          } while (nextHref.length > 0);
        }
        else {
          const listStreamDrilldown = await getListAsStreamGroupDrillDown({
            siteUrl: isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory.webUrl,
            sharePointListId: currentDirectory.sharePointListId,
            viewId: selectedView.Id,
            groupString: group.urlencoded,
            sortProps: sortProps,
            accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
            serverRelativePath,
            nextHref,
            filterSections,
            searchValue
          }).unwrap();

          const groupItems: any[] = searchValue?.value?.trim().length > 0 && CacheHelper.get(DocubirdCache.isStrictSearch)
            ? filterStrictSearchResults(isSharePointList, listStreamDrilldown.ListData.Row, searchValue.value)
            : listStreamDrilldown.ListData.Row as any[];

          newGridProps.items = newGridProps.items ? [...newGridProps.items].concat(groupItems) : [...groupItems];

          nextHref = listStreamDrilldown.ListData.NextHref ? listStreamDrilldown.ListData.NextHref : '';

          let currentGroup: IGroup = null;

          if (!group.parentKey) {
            currentGroup = newGridProps.groups.find(x => x.key === group.key);
          }
          else {
            const parentGroup = newGridProps.groups.find(x => x.key === group.parentKey);
            if (parentGroup) {
              currentGroup = parentGroup.children.find(x => x.key === group.key);
            }
          }

          if (currentGroup) {
            const allItemsCount = newGridProps.items?.length > 0 ? newGridProps.items?.length : 0;
            const newItemsCount = groupItems.length;

            currentGroup.startIndex = allItemsCount - newItemsCount
            currentGroup.count = newItemsCount;
          }

          setGridProps({ ...newGridProps, items: [...newGridProps.items] });
        }
      }
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (fetchGroupedDrillDownData)");
    }
  }

  const fetchGroupedDrillDownDataAllGroups = async () => {
    try {
      await fetchGroupedDataInBatches();
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (fetchGroupedDrillDownDataAllGroups)");
    }
  }

  const fetchGroupedDataInBatches = async () => {
    let batchSize = 4;
    const newGridProps = { ...gridProps }

    let requestCurrentDirectoryId = currentDirectory.id;
    let requestViewId = selectedView.Id;

    let position = 0;
    let results = [];
    let items: any[] = [];

    while (position < newGridProps.groups.length) {
      if (requestCurrentDirectoryId === currentDirectoryId.current && requestViewId === selectedViewId.current && !isAllCollapsedRef.current) {
        const itemsForBatch = newGridProps.groups.slice(position, position + batchSize);

        results = await Promise.all(itemsForBatch.map(item => fetchSingleGroupData(item)));
        position += batchSize;

        for (let index = 0; index < itemsForBatch.length; index++) {
          let batchGroup = itemsForBatch[index];
          let currentGroup: IGroup = null;

          if (!batchGroup.parentKey) {
            currentGroup = newGridProps.groups.find(x => x.key === batchGroup.key);
          }
          else {
            const parentGroup = newGridProps.groups.find(x => x.key === batchGroup.parentKey);
            if (parentGroup) {
              currentGroup = parentGroup.children.find(x => x.key === batchGroup.key);
            }
          }

          if (currentGroup) {
            const allItemsCount = items?.length > 0 ? items?.length : 0;
            const newItemsCount = (results[index]).length;

            currentGroup.startIndex = allItemsCount
            currentGroup.count = newItemsCount;
          }

          items = items.concat(results[index]);
        }
        setGridProps({ ...newGridProps, items: [...items] });

      }
      else {
        position = newGridProps.groups.length
      }
    }

    return results;
  }

  const fetchSingleGroupData = async (group: IGridGroup) => {
    if (group.count === group.totalCount) {
      return gridProps.items.slice(group.startIndex, group.startIndex + group.count);
    }

    let nextHref = '';
    let requestCurrentDirectoryId = currentDirectory.id;
    let requestViewId = selectedView.Id;
    let items: any[] = [];

    let serverRelativePath = '';
    if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) {
      serverRelativePath = currentDirectory.serverRelativePath
    }
    else if (currentDirectory?.directory?.length > 0 && currentDirectory?.parentId) {
      let driveId = isInOneDrive.current ? oneDriveRootDetails.driveId : (currentDirectory.parentId ? currentDirectory.parentId : currentDirectory.id);
      const results = await getItemWebUrl({ driveId, itemId: currentDirectory?.id, accessToken: await authModule.getGraphAccessToken() }).unwrap();
      const resolvedPath = tryGetPathname(results.listItem?.webUrl || results.webUrl);
      serverRelativePath = resolvedPath ? tryDecodeUriComponent(resolvedPath) : currentDirectory?.serverRelativePath ?? "";
    }

    do {
      if (requestCurrentDirectoryId === currentDirectoryId.current && requestViewId === selectedViewId.current && !isAllCollapsedRef.current) {
        const listStreamDrilldown = await getListAsStreamGroupDrillDown2({
          siteUrl: isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory.webUrl,
          sharePointListId: currentDirectory.sharePointListId,
          viewId: selectedView.Id,
          sortProps: gridProps.sort,
          accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          serverRelativePath,
          filterField: group.filterField,
          filterValue: group.filterValue,
          nextHref,
          searchValue
        }).unwrap();

        const groupItems: any[] = searchValue?.value?.trim().length > 0 && CacheHelper.get(DocubirdCache.isStrictSearch)
          ? filterStrictSearchResults(isSharePointList, listStreamDrilldown.ListData.Row, searchValue.value)
          : listStreamDrilldown.ListData.Row as any[];

        items = items ? [...items].concat(groupItems) : [...groupItems];
        nextHref = listStreamDrilldown.ListData.NextHref ? listStreamDrilldown.ListData.NextHref : '';
      }
      else {
        nextHref = '';
      }
    } while (nextHref.length > 0);

    return items;
  }

  const getGroupNameValue = (groupName: string, groupRow: any) => {
    const isObject = typeof groupRow[groupName] === 'object';

    if (isObject && groupRow[groupName].length > 0) {
      return groupRow[groupName][0].title ?? groupRow[groupName][0].lookupValue;
    }

    if (groupRow[`${groupName}.groupdisp`]) {
      const isValidDate = dayjs(groupRow[`${groupName}.groupdisp`]).isValid();
      return isValidDate ? dayjs(groupRow[`${groupName}.groupdisp`]).format('D-M-YYYY') : groupRow[`${groupName}.groupdisp`];
    }

    if (groupRow[groupName] && groupRow[groupName].length > 0) {
      return groupRow[groupName];
    }

    if (groupRow[`${groupName}.singleurlencoded`] && groupRow[`${groupName}.singleurlencoded`].length > 0) {
      return tryDecodeUriComponent(groupRow[`${groupName}.singleurlencoded`]);
    }

    return languageStrings.Unassigned;
  }

  const getGroupDisplayName = (groupName: string): string => {
    const groupDisplayName: string = listFields.current?.find(x => x.RealFieldName === groupName)?.DisplayName
      ?? listColumnsSchema.current?.value.find(x => x.InternalName === groupName)?.Title;

    return groupDisplayName ?? languageStrings.Unassigned;
  }

  const getViewsAndColumns = async (): Promise<IListDetails> => {
    setIsFetchingData(true);

    let documentSetContentTypeId: string;
    if (!isInOneDrive.current) {
      if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
        documentSetContentTypeId = currentDirectory.contentTypeId;
      }
      else {
        documentSetContentTypeId =
          currentDirectory?.parentDocumentSetContentTypeId
          ??
          workingDirectories?.find(dir => dir?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet))?.contentTypeId;
      }
    }

    const accessToken: string = isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken();
    const [listColumns, views, contentTypes, listSchema, documentSetSettings] = await Promise.all(
      [
        getListColumns(),
        getRawViews(accessToken),
        !isInOneDrive.current ? getSharePointContentTypesWithFields(accessToken, currentDirectory.webUrl, currentDirectory.sharePointListId) : null,
        !isInOneDrive.current ? getSharepointListSchema(accessToken, currentDirectory.webUrl, currentDirectory.sharePointListId) : null,
        documentSetContentTypeId ? SharepointContentTypeHelper.getDocumentSetSettings(accessToken, currentDirectory.webUrl, currentDirectory.sharePointListId, documentSetContentTypeId) : null
      ]
    );

    if (Array.isArray(listSchema?.Field)) {
      listFields.current = listSchema.Field;
    }
    listColumnsSchema.current = listColumns;

    if (documentSetContentTypeId) {
      isInDocumentSet.current = true;
      documentSetDefaultViews.current = [{ contentTypeId: documentSetContentTypeId, viewId: documentSetSettings?.viewId }];
    }

    const defaultViewId: string = directoryViewModule.getDirectoryView(currentDirectory?.id)?.viewId ?? documentSetSettings?.viewId;

    setRawViews(views);
    if (Array.isArray(contentTypes) && contentTypes.length > 0) {
      // setContentTypes(contentTypes);
      setContentTypes(contentTypes.map(ct => ({ Id: ct.Id, Name: ct.Name, Sealed: ct.Sealed, RequiredFields: ct.Fields?.filter(field => field.InternalName !== sharePointInternalFileName && field.Required && !field.Hidden && field.Group !== "_Hidden") })));
    }
    getNewDocumentTemplates(listSchema?.NewDocumentTemplates, contentTypes);
    getViewsWithColumns(views, defaultViewId);

    return { forceCheckout: listSchema?.ForceCheckout === "1" };
  }

  const getRawViews = async (accessToken: string) => {
    try {
      if (currentDirectory && currentDirectory.sharePointListId) {
        return (await getSharePointListViews(
          accessToken,
          isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory.webUrl,
          currentDirectory.sharePointListId
        ))
          ?.map(view => {
            return ({
              ...view,
              Title: view.Title,
              DefaultView: view['DefaultView'],
              Id: view.Id
            })
          });
      }
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (getRawViews)");
    }
    return null;
  }

  const populateTileDisplayFields = React.useCallback((tileDisplayFields: ITileDisplayField[], listCol: any, fieldName?: string) => {
    const listColSchema = listColumnsSchema.current?.value.find((x) => x.InternalName === listCol.RealFieldName);
    if (listCol.FieldType === "Calculated") {
      if (listColSchema) {
        try {
          const colSchema: any = convertMetadataToFields(listColSchema);
          listCol.Format = colSchema.xml.Format;
          listCol.FieldType = colSchema.xml.ResultType;
        } catch (error) {
          // Ignore
        }
      }
    }
    else if (listCol.FieldType === "DateTime" && listCol.FriendlyDateTimeEnabled === "True") {
      listCol.Format = "DateOnly";
    }

    tileDisplayFields.push({
      title: listCol.DisplayName,
      name: fieldName === sharePointInternalFileName ? sharePointInternalFileName : listCol.RealFieldName,
      type: listCol.FieldType,
      format: listCol.Format,
      isCustomFormat: Boolean(listColSchema.CustomFormatter)
    });
  }, []);

  const getViewsWithColumns = (views: any[], defaultViewId: string) => {
    const viewsColumns: IViewsDropdown[] = [];
    views?.forEach((view, index) => parseString(view.ListViewXml, (err, result) => {
      if (result) {
        let tileDisplayFields: ITileDisplayField[];
        if (view.ViewType2 === ViewTypes.TILES) {
          if (Array.isArray(result.View.CustomFormatter) && result.View.CustomFormatter.length > 0) {
            try {
              const tileDisplayFieldNames: Set<string> = getTileDisplayFieldNames(JSON.parse(result.View.CustomFormatter[0]));
              tileDisplayFields = [];
              tileDisplayFieldNames.forEach(fieldName => {
                let listCol: any;
                if (fieldName === sharePointInternalFileName) {
                  listCol = listFields.current?.find((x => x.RealFieldName === fieldName || x.RealFieldName === "LinkFilename"));
                }
                else {
                  listCol = listFields.current?.find((x => x.RealFieldName === fieldName));
                }

                if (listCol) {
                  populateTileDisplayFields(tileDisplayFields, listCol, fieldName);
                }
              });
            } catch (error) {
              // Ignore
            }
          }
          else if (isSharePointList) {
            tileDisplayFields = [];
            for (const listCol of listFields.current) {
              populateTileDisplayFields(tileDisplayFields, listCol);
            }
          }
        }

        viewsColumns.push(
          {
            Id: view.Id,
            text: view.Title,
            key: `${view.Title}-${index}`,
            query: result.View.Query[0],
            fields: result.View.ViewFields[0].FieldRef
              .map(f => f.$),
            siteId: '',
            webUrl: result.View.$.Url,
            default: view.DefaultView,
            viewType: view.ViewType2,
            tileDisplayFields: tileDisplayFields
          });
      }
      if (err) {
        logger(err, "useContentManagerBody.tsx (getViewsWithColumns)");
      }
    }));

    let defaultView: IViewsDropdown;
    if (defaultViewId) {
      defaultView = viewsColumns.find(x => x.Id === defaultViewId);
    }
    if (!defaultView) {
      defaultView = viewsColumns.find(x => x.default === true);
    }

    const reportView: IViewsDropdown = {
      ...defaultView,
      default: false,
      key: "Minor_Draft_Versions",
      text: languageStrings.MinorVersions,
      isReport: true
    };

    setViewsWithColumns([...viewsColumns, reportView]);

    setSelectedView(defaultView ? defaultView : viewsColumns[0]);
  }

  const getListColumns = async () => {
    try {
      return await getListColumnsSchema({
        siteUrl: isInOneDrive.current ? oneDriveRootDetails.sharePointSiteUrl : currentDirectory.webUrl,
        sharePointListId: currentDirectory.sharePointListId,
        accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken()
      }).unwrap();
    } catch (error) {
      logger(error, "useContentManagerBody.tsx (getListColumns)");
    }
    return null;
  }

  const getOrderByGroupByFromView = (gridProps: GridProps) => {
    const sortProps: GridSortProps[] = [];
    gridProps.sort = null;
    gridProps.groupProps = null;

    const orderBy = selectedView?.query?.OrderBy;
    orderBy?.forEach(ref => {
      ref.FieldRef.forEach(fieldRef => {
        sortProps.push({
          decending: fieldRef?.$?.Ascending === 'FALSE' ? true : false,
          field: (fieldRef?.$?.Name as string)
        });
      });
    });

    gridProps.sort = sortProps;


    //Groups
    const groupProps: GridGroupProps = { names: [] }
    const groupBy = selectedView?.query?.GroupBy;
    if (groupBy?.length > 0) {
      const grouping = groupBy[0];
      groupProps.collapse = grouping.$?.Collapse === 'TRUE';
      groupProps.limit = +grouping.$?.GroupLimit;

      grouping.FieldRef.forEach(fieldRef => {
        groupProps.names.push(fieldRef?.$?.Name as string);
      });

      gridProps.groupProps = groupProps;
    }
  }

  const updateColumnSortProps = (column: IGridColumn, gridProps: GridProps, colSchema: any) => {
    gridProps.sort.forEach(sortProp => {
      if ((sortProp.field === colSchema.InternalName) || (sortProp.field === colSchema.xml.DisplayNameSrcField)) {
        column.isSorted = true;
        column.isSortedDescending = sortProp.decending === true
      }
    });
  }

  const convertMetadataToFields = item => {
    let xmlData: any = {};
    parseString(item.SchemaXml, (err, result) => {
      if (result) {
        xmlData = result.Field.$;
      }
    });

    if (item.Title) {
      xmlData.DisplayName = item.Title;
    }

    return {
      Choices: item.Choices,
      DefaultValue: item.DefaultValue,
      CurrentValue: item.DefaultValue,
      isValid: true,
      Id: item.Id,
      InternalName: item.InternalName,
      xml: xmlData
    };
  }

  const onSortColumn = (column?: IGridColumn, uiSearch?: boolean) => {
    const newGridProps: GridProps = { ...gridProps };
    newGridProps.sort = [];
    let isSortDesc = column.isSortedDescending ? !column.isSortedDescending : true;

    const newColumns: IGridColumn[] = []

    newGridProps.columns.forEach(col => {
      let newCol: IGridColumn = { ...col };

      if (newCol.internalName === column.internalName) {
        newCol.isSorted = true;
        newCol.isSortedDescending = isSortDesc;
        newGridProps.sort.push({
          field: column.internalName,
          decending: isSortDesc
        });
      } else {
        newCol.isSorted = null;
        newCol.isSortedDescending = null
      }

      newColumns.push(newCol);
    });

    newGridProps.columns = [...newColumns];

    if (!uiSearch) {
      setGridProps({ ...newGridProps });
      dispatch(toggleContentManagerReload());
    } else {
      const newItems = _copyAndSort([...newGridProps.items], newGridProps.sort[0].field!, newGridProps.sort[0].decending);
      setGridProps({ ...newGridProps, items: [...newItems] });
    }
  }

  return {
    isFetchingData,
    gridProps,
    viewsWithColumns,
    selectedView,
    listColumnsSchema,
    scrollToTopToggle,
    isAllCollapsedRef,
    serverRelPath,
    folderPath,
    newDocumentTemplates,
    libraryMinVerEnabled,
    listTitle,
    listFields: listColumnsSchema.current,
    listHttpRoot: listHttpRoot.current,
    setSelectedView,
    fetchGroupedDrillDownData,
    fetchGroupedDrillDownDataAllGroups,
    onSortColumn,
    reloadGrid,
    fetchListStreamNextPage,
    setGridProps,
    setIsFetchingData,
    getTimeZoneFormattedDate,
    onFolderClick
  }
}
